import { IconSizes } from "atoms/constants/icon-sizes";
import Loader from "atoms/loaders/loader";
import PublicationRecord from "models/view-models/publication-record";
import PublicationMetaCard from "molecules/cards/publication-meta-card";
import CollapsePanel from "molecules/collapse-panel/collapse-panel";
import VirtualTableOfContentsAnnex from "molecules/virtual-table-of-contents-items/virtual-table-of-contents-annex";
import VirtualTableOfContentsChapter from "molecules/virtual-table-of-contents-items/virtual-table-of-contents-chapter";
import NotebookTableOfContentsHeaderButton from "pages/notebooks/notebook-reference-panel/bookview/table-of-contents/notebook-table-of-contents-header-button";
import React from "react";
import useBookviewTableOfContents from "utilities/hooks/domain/publications/use-bookview-table-of-contents";
import { useErrorBoundary } from "utilities/hooks/use-error-boundary";
import usePageErrors from "utilities/hooks/use-page-errors";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface TableOfContentsProps {
    isPublic?: boolean;
    publication: PublicationRecord;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const TableOfContents: React.FunctionComponent<TableOfContentsProps> = (
    props: TableOfContentsProps
) => {
    const { publication } = props;

    // -----------------------------------------------------------------------------------------
    // #region Constants
    // -----------------------------------------------------------------------------------------

    const CSS_CLASS_NAME =
        "c-notebook-reference-panel-bookview__table-of-contents";

    // #endregion Constants

    // -----------------------------------------------------------------------------------------
    // #region Custom Hooks
    // -----------------------------------------------------------------------------------------

    const { pageErrors } = usePageErrors();

    const { loading, tableOfContents } = useBookviewTableOfContents();
    const { annexes, chapters, publicationMetadata } = tableOfContents;

    const metadataWithPublication = publicationMetadata?.map((p) =>
        p.with({ publication })
    );

    // #endregion Custom Hooks

    // -----------------------------------------------------------------------------------------
    // #region State
    // -----------------------------------------------------------------------------------------

    // #endregion State

    // -----------------------------------------------------------------------------------------
    // #region Side-Effects
    // -----------------------------------------------------------------------------------------

    useErrorBoundary(pageErrors);

    // #endregion Side-Effects

    // -----------------------------------------------------------------------------------------
    // #region Render
    // -----------------------------------------------------------------------------------------
    if (publication == null) return null;

    if (loading) {
        return (
            <div className={CSS_CLASS_NAME}>
                <Loader />
            </div>
        );
    }

    return (
        <div className={CSS_CLASS_NAME}>
            <div>
                <PublicationMetaCard publication={publication} />
            </div>

            {metadataWithPublication?.map((metadata) => (
                <div
                    className={`${CSS_CLASS_NAME}__meta -empty`}
                    key={metadata.id}>
                    <CollapsePanel
                        iconSize={IconSizes.Base}
                        collapse={true}
                        buttonAriaText={metadata.getTypeDisplayName()}
                        keepAligned={true}
                        panelTop={
                            <NotebookTableOfContentsHeaderButton
                                dataTestId={`${metadata.type}-link`}
                                displayTitle={metadata.getTypeDisplayName()}
                                publicationComponentType={metadata.type}
                            />
                        }
                    />
                </div>
            ))}

            {chapters?.map((chapter) => (
                <VirtualTableOfContentsChapter
                    allCollapsed={true}
                    chapter={chapter}
                    dataTestId="chapter-link"
                    key={chapter.id}
                    publicationId={publication.id ?? 0}
                />
            ))}

            <VirtualTableOfContentsAnnex
                allCollapsed={true}
                annexes={annexes ?? []}
                dataTestId="annex-link"
                publication={publication}
            />
        </div>
    );

    // #endregion Render
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default TableOfContents;

// #endregion Exports
